import { create } from "zustand";
import {
  getTags,
  createTag as createTagApi,
  updateTag as updateTagApi,
  deleteTag as deleteTagApi,
} from "../api/tags";
import type { Tag, CreateTagRequest, UpdateTagRequest } from "@/types/tags";

interface TagState {
  businessTags: Tag[];
  loading: boolean;
  error: string | null;
  initialized: boolean;

  // Actions
  initialize: () => Promise<void>;
  createTag: (data: CreateTagRequest) => Promise<Tag>;
  updateTag: (id: string, updates: UpdateTagRequest) => Promise<Tag>;
  deleteTag: (id: string) => Promise<void>;
  refreshTags: () => Promise<void>;
  setTags: (tags: Tag[]) => void;
  reset: () => void;
}

export const useTagStore = create<TagState>((set, get) => ({
  businessTags: [],
  loading: false,
  error: null,
  initialized: false,

  initialize: async () => {
    if (get().initialized || get().loading) {
      return;
    }

    set({ loading: true, error: null });

    try {
      const tags = await getTags();
      set({
        businessTags: tags,
        loading: false,
        initialized: true,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to initialize tags";
      set({ error: errorMessage, loading: false, initialized: true });
      console.error("[TagStore] Initialization failed:", error);
      throw error;
    }
  },

  createTag: async (data: CreateTagRequest) => {
    try {
      const newTag = await createTagApi(data);
      const { businessTags } = get();
      set({ businessTags: [...businessTags, newTag] });
      return newTag;
    } catch (error) {
      console.error("[TagStore] Failed to create tag:", error);
      throw error;
    }
  },

  updateTag: async (id: string, updates: UpdateTagRequest) => {
    try {
      const updatedTag = await updateTagApi(id, updates);
      const { businessTags } = get();
      const newTags = businessTags.map((tag) =>
        tag.id === id ? updatedTag : tag
      );
      set({ businessTags: newTags });
      return updatedTag;
    } catch (error) {
      console.error("[TagStore] Failed to update tag:", error);
      throw error;
    }
  },

  deleteTag: async (id: string) => {
    try {
      await deleteTagApi(id);
      const { businessTags } = get();
      const newTags = businessTags.filter((tag) => tag.id !== id);
      set({ businessTags: newTags });
    } catch (error) {
      console.error("[TagStore] Failed to delete tag:", error);
      throw error;
    }
  },

  refreshTags: async () => {
    try {
      const tags = await getTags();
      set({ businessTags: tags });
    } catch (error) {
      console.error("[TagStore] Failed to refresh tags:", error);
      throw error;
    }
  },

  setTags: (tags: Tag[]) => {
    set({ businessTags: tags });
  },

  reset: () => {
    set({
      businessTags: [],
      loading: false,
      error: null,
      initialized: false,
    });
  },
}));
